/** The requirements the evidence card can ask an employee to satisfy. */
export type EvidenceStepKey = 'grades' | 'gpa' | 'documents' | 'terms';

/** One row of the evidence card's checklist. */
export interface EvidenceChecklistStep {
  key: EvidenceStepKey;
  /**
   * Noun phrase naming what is still owed, e.g. "your final grade". Read in the
   * collapsed summary and in the blocked-submit reason, so it has to read
   * mid-sentence rather than as a heading.
   */
  shortLabel: string;
  isDone: boolean;
}

export interface EvidenceChecklistInputs {
  /**
   * The employer override that waives eligibility rules for this application.
   * It short-circuits the submit gate, so counting requirements it does not
   * enforce would put an incomplete meter beside an enabled Submit.
   */
  isWaived: boolean;
  isCourseGradeRequired: boolean;
  isMissingCourseGrades: boolean;
  isGpaRequired: boolean;
  hasGpa: boolean;
  areDocumentsRequired: boolean;
  hasRequiredDocuments: boolean;
  hasTerms: boolean;
  hasAgreedTerms: boolean;
  /** Localized noun phrases, keyed by step. */
  shortLabels: Record<EvidenceStepKey, string>;
}

export interface EvidenceChecklist {
  steps: EvidenceChecklistStep[];
  doneCount: number;
  totalCount: number;
  /** Short labels of the outstanding steps, in step order. */
  remaining: string[];
  isComplete: boolean;
}

/**
 * Builds the evidence card's checklist from the same flags the submit gate
 * reads, so the count beside the card can never disagree with what Submit
 * actually enforces.
 *
 * Only requirements become steps. A template that shows the uploader without
 * requiring a document contributes no documents step, because a count that
 * includes optional work misreports how much is left — the failure mode the
 * card's "0 / 1 required" counter already had when it counted rows the gate
 * ignored.
 *
 * Employer eligibility locks are deliberately absent: they block submission
 * without being anything the employee can act on, so they belong in the
 * blocked-submit message rather than in a checklist of remaining work. The
 * opposite override, `isWaived`, empties the checklist entirely.
 */
export function buildEvidenceChecklist(inputs: EvidenceChecklistInputs): EvidenceChecklist {
  const steps: EvidenceChecklistStep[] = [];

  // Nothing is enforced, so nothing is counted and every row reads as optional.
  if (inputs.isWaived) {
    return { steps, doneCount: 0, totalCount: 0, remaining: [], isComplete: true };
  }

  if (inputs.isCourseGradeRequired) {
    steps.push({
      key: 'grades',
      shortLabel: inputs.shortLabels.grades,
      isDone: !inputs.isMissingCourseGrades,
    });
  }

  if (inputs.isGpaRequired) {
    steps.push({ key: 'gpa', shortLabel: inputs.shortLabels.gpa, isDone: inputs.hasGpa });
  }

  if (inputs.areDocumentsRequired) {
    steps.push({
      key: 'documents',
      shortLabel: inputs.shortLabels.documents,
      isDone: inputs.hasRequiredDocuments,
    });
  }

  if (inputs.hasTerms) {
    steps.push({
      key: 'terms',
      shortLabel: inputs.shortLabels.terms,
      isDone: inputs.hasAgreedTerms,
    });
  }

  const remaining = steps.filter((step) => !step.isDone).map((step) => step.shortLabel);

  return {
    steps,
    doneCount: steps.length - remaining.length,
    totalCount: steps.length,
    remaining,
    isComplete: remaining.length === 0,
  };
}

/**
 * Joins outstanding step labels into one prose fragment, e.g.
 * "your final grade and one receipt", for the collapsed summary and the
 * blocked-submit reason. Returns an empty string when nothing is outstanding.
 */
export function summarizeRemaining(
  remaining: readonly string[],
  locale: string | undefined
): string {
  if (remaining.length === 0) {
    return '';
  }
  return new Intl.ListFormat(locale ?? 'en-us', {
    style: 'long',
    type: 'conjunction',
  }).format(remaining);
}

/**
 * Whether a step applies to this application at all. A step absent from the
 * checklist is not rendered as a row, so an optional uploader still draws while
 * contributing nothing to the count.
 */
export function hasStep(checklist: EvidenceChecklist, key: EvidenceStepKey): boolean {
  return checklist.steps.some((step) => step.key === key);
}

/** Whether a step exists and is satisfied. Absent steps read as done. */
export function isStepDone(checklist: EvidenceChecklist, key: EvidenceStepKey): boolean {
  const step = checklist.steps.find((entry) => entry.key === key);
  return step ? step.isDone : true;
}
